import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import API_URL from '../config';

function getAuthHeader() {
  const token = localStorage.getItem('authToken');
  return token ? { Authorization: `Bearer ${token}` } : {};
}

const commsApi = {
  list: (reservationId) =>
    axios.get(`${API_URL}/api/reservations/${reservationId}/communications`, {
      headers: getAuthHeader(),
    }).then((r) => r.data),

  availableTemplates: (reservationId) =>
    axios.get(`${API_URL}/api/reservations/${reservationId}/available-templates`, {
      headers: getAuthHeader(),
    }).then((r) => r.data),

  send: (reservationId, data) =>
    axios.post(`${API_URL}/api/reservations/${reservationId}/send`, data, {
      headers: getAuthHeader(),
    }).then((r) => r.data),

  preview: (reservationId, data) =>
    axios.post(`${API_URL}/api/reservations/${reservationId}/send?preview=true`, data, {
      headers: getAuthHeader(),
    }).then((r) => r.data),

  resend: (reservationId, jobId) =>
    axios.post(`${API_URL}/api/reservations/${reservationId}/resend/${jobId}`, {}, {
      headers: getAuthHeader(),
    }).then((r) => r.data),

  context: (reservationId) =>
    axios.get(`${API_URL}/api/reservations/${reservationId}/context`, {
      headers: getAuthHeader(),
    }).then((r) => r.data),

  cancelJob: (jobId) =>
    axios.post(`${API_URL}/api/email-jobs/${jobId}/cancel`, {}, {
      headers: getAuthHeader(),
    }).then((r) => r.data),

  requeueJob: (jobId) =>
    axios.post(`${API_URL}/api/email-jobs/${jobId}/requeue`, {}, {
      headers: getAuthHeader(),
    }).then((r) => r.data),
};

export function useReservationComms(reservationId) {
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['reservation-comms', reservationId],
    queryFn: () => commsApi.list(reservationId),
    enabled: !!reservationId,
  });

  return {
    communications: data?.data || [],
    isLoading,
    error,
    refetch,
  };
}

export function useAvailableTemplates(reservationId) {
  const { data, isLoading, error } = useQuery({
    queryKey: ['available-templates', reservationId],
    queryFn: () => commsApi.availableTemplates(reservationId),
    enabled: !!reservationId,
  });

  return {
    templateCategories: data?.data || [],
    isLoading,
    error,
  };
}

export function useReservationContext(reservationId) {
  const { data, isLoading, error } = useQuery({
    queryKey: ['reservation-context', reservationId],
    queryFn: () => commsApi.context(reservationId),
    enabled: !!reservationId,
  });

  return {
    context: data?.data || null,
    isLoading,
    error,
  };
}

export function useSendEmail(reservationId) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data) => commsApi.send(reservationId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['reservation-comms', reservationId] });
    },
  });
}

export function usePreviewEmail(reservationId) {
  return useMutation({
    mutationFn: (data) => commsApi.preview(reservationId, data),
  });
}

export function useResendEmail(reservationId) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (jobId) => commsApi.resend(reservationId, jobId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['reservation-comms', reservationId] });
    },
  });
}

export function useCancelCommJob() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (jobId) => commsApi.cancelJob(jobId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['reservation-comms'] });
      queryClient.invalidateQueries({ queryKey: ['email-jobs'] });
    },
  });
}

export function useRequeueCommJob() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (jobId) => commsApi.requeueJob(jobId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['reservation-comms'] });
      queryClient.invalidateQueries({ queryKey: ['email-jobs'] });
    },
  });
}
